import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Barcode, Camera, Search, Loader2, X, Plus, Minus, Save, Package, Check, AlertCircle, History } from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../config/api';

// Helper to get auth token
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const MEAL_TYPES = [
  { id: 'breakfast', label: 'Breakfast', emoji: '🌅' },
  { id: 'lunch', label: 'Lunch', emoji: '☀️' },
  { id: 'dinner', label: 'Dinner', emoji: '🌙' },
  { id: 'snack', label: 'Snack', emoji: '🍎' }
];

export default function BarcodeScanner({ childId, onProductLogged }) {
  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState('scan'); // scan, search, result, recent
  const [loading, setLoading] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [product, setProduct] = useState(null);
  const [mealType, setMealType] = useState('snack');
  const [servings, setServings] = useState(1);
  const [recentProducts, setRecentProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (showModal && mode === 'recent') {
      fetchRecentProducts();
    }
    return () => stopCamera();
  }, [showModal, mode]);

  const fetchRecentProducts = async () => {
    try {
      const res = await axios.get(`${API}/barcode/recent`, { headers: getAuthHeaders() });
      setRecentProducts(res.data.products || []);
    } catch (err) {
      console.error('Failed to fetch recent products');
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      toast.error('Could not access camera');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const lookupBarcode = async (code) => {
    if (!code || code.length < 8) {
      toast.error('Please enter a valid barcode (8+ digits)');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${API}/barcode/lookup/${code}`, { headers: getAuthHeaders() });
      setProduct(res.data.product);
      setMode('result');
      stopCamera();
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('Product not found in database');
      } else {
        toast.error('Failed to lookup barcode');
      }
    } finally {
      setLoading(false);
    }
  };

  const searchProducts = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    try {
      const res = await axios.get(`${API}/barcode/search`, {
        params: { query: searchQuery },
        headers: getAuthHeaders()
      });
      setSearchResults(res.data.products || []);
    } catch (err) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const selectProduct = async (barcode) => {
    await lookupBarcode(barcode);
  };

  const logProduct = async () => {
    if (!product) return;

    setSaving(true);
    const formData = new FormData();
    formData.append('child_id', childId);
    formData.append('barcode', product.barcode);
    formData.append('meal_type', mealType);
    formData.append('servings', servings);
    formData.append('date', new Date().toISOString());

    try {
      await axios.post(`${API}/barcode/log-product`, formData, { headers: getAuthHeaders() });
      toast.success('Product logged! 🎉');
      setShowModal(false);
      resetState();
      if (onProductLogged) onProductLogged();
    } catch (err) {
      toast.error('Failed to log product');
    } finally {
      setSaving(false);
    }
  };

  const resetState = () => {
    setMode('scan');
    setBarcodeInput('');
    setSearchQuery('');
    setSearchResults([]);
    setProduct(null);
    setServings(1);
    stopCamera();
  };

  const getNutriScoreColor = (grade) => {
    const colors = {
      'a': 'bg-green-500',
      'b': 'bg-lime-500',
      'c': 'bg-yellow-500',
      'd': 'bg-orange-500',
      'e': 'bg-red-500'
    };
    return colors[grade?.toLowerCase()] || 'bg-zinc-500';
  };

  const scaledValue = (val) => Math.round((val || 0) * servings * 10) / 10;

  return (
    <>
      <Button
        onClick={() => { resetState(); setShowModal(true); }}
        className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white"
        data-testid="barcode-scan-btn"
      >
        <Barcode className="h-4 w-4 mr-2" />
        Scan Barcode
      </Button>

      {showModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                  <Barcode className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="font-bold text-white">Barcode Scanner</h2>
                  <p className="text-xs text-zinc-400">
                    {mode === 'scan' && 'Enter barcode or search by name'}
                    {mode === 'search' && 'Search for products'}
                    {mode === 'result' && 'Product found'}
                    {mode === 'recent' && 'Recently scanned'}
                  </p>
                </div>
              </div>
              <button onClick={() => { setShowModal(false); resetState(); }} className="text-zinc-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Mode Tabs */}
            {mode !== 'result' && (
              <div className="p-2 border-b border-zinc-800 flex gap-2">
                <button
                  onClick={() => setMode('scan')}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    mode === 'scan' ? 'bg-blue-500 text-white' : 'bg-zinc-800 text-zinc-400'
                  }`}
                >
                  <Barcode className="h-4 w-4" /> Barcode
                </button>
                <button
                  onClick={() => setMode('search')}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    mode === 'search' ? 'bg-blue-500 text-white' : 'bg-zinc-800 text-zinc-400'
                  }`}
                >
                  <Search className="h-4 w-4" /> Search
                </button>
                <button
                  onClick={() => setMode('recent')}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    mode === 'recent' ? 'bg-blue-500 text-white' : 'bg-zinc-800 text-zinc-400'
                  }`}
                >
                  <History className="h-4 w-4" /> Recent
                </button>
              </div>
            )}

            <div className="p-4 max-h-[60vh] overflow-y-auto">
              {/* Scan Mode */}
              {mode === 'scan' && (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-zinc-500 mb-2 block">Enter Barcode Number</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={barcodeInput}
                        onChange={(e) => setBarcodeInput(e.target.value.replace(/\D/g, ''))}
                        placeholder="e.g., 5000159461122"
                        className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white text-lg tracking-wider"
                        onKeyDown={(e) => e.key === 'Enter' && lookupBarcode(barcodeInput)}
                      />
                      <Button
                        onClick={() => lookupBarcode(barcodeInput)}
                        disabled={loading || barcodeInput.length < 8}
                        className="bg-blue-500 px-6"
                      >
                        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
                      </Button>
                    </div>
                  </div>

                  <div className="text-center py-6 border-2 border-dashed border-zinc-700 rounded-xl">
                    <Package className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
                    <p className="text-zinc-400 text-sm">Find the barcode on your food packaging</p>
                    <p className="text-zinc-500 text-xs mt-1">Usually on the back, near nutrition info</p>
                  </div>

                  <p className="text-xs text-zinc-500 text-center">
                    Powered by Open Food Facts database • 2.5M+ products
                  </p>
                </div>
              )}

              {/* Search Mode */}
              {mode === 'search' && (
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search products..."
                      className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white"
                      onKeyDown={(e) => e.key === 'Enter' && searchProducts()}
                    />
                    <Button onClick={searchProducts} disabled={loading} className="bg-blue-500">
                      {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
                    </Button>
                  </div>

                  {searchResults.length > 0 && (
                    <div className="space-y-2">
                      {searchResults.map((p, i) => (
                        <button
                          key={i}
                          onClick={() => selectProduct(p.barcode)}
                          className="w-full flex items-center gap-3 bg-zinc-800/50 hover:bg-zinc-800 rounded-lg p-3 text-left transition-all"
                        >
                          {p.image_url ? (
                            <img src={p.image_url} alt="" className="w-12 h-12 object-cover rounded" />
                          ) : (
                            <div className="w-12 h-12 bg-zinc-700 rounded flex items-center justify-center">
                              <Package className="h-6 w-6 text-zinc-500" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate">{p.name}</p>
                            <p className="text-xs text-zinc-500">{p.brand || 'Generic'}</p>
                          </div>
                          <div className="text-right text-xs">
                            <div className="text-white">{Math.round(p.calories)} cal</div>
                            <div className="text-zinc-500">per 100g</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Recent Mode */}
              {mode === 'recent' && (
                <div className="space-y-2">
                  {recentProducts.length === 0 ? (
                    <div className="text-center py-8">
                      <History className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
                      <p className="text-zinc-400">No recent scans</p>
                      <p className="text-xs text-zinc-500">Products you scan will appear here</p>
                    </div>
                  ) : (
                    recentProducts.map((p, i) => (
                      <button
                        key={i}
                        onClick={() => selectProduct(p.barcode)}
                        className="w-full flex items-center gap-3 bg-zinc-800/50 hover:bg-zinc-800 rounded-lg p-3 text-left transition-all"
                      >
                        {p.image_url ? (
                          <img src={p.image_url} alt="" className="w-12 h-12 object-cover rounded" />
                        ) : (
                          <div className="w-12 h-12 bg-zinc-700 rounded flex items-center justify-center">
                            <Package className="h-6 w-6 text-zinc-500" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{p.name}</p>
                          <p className="text-xs text-zinc-500">{p.brand}</p>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}

              {/* Result Mode */}
              {mode === 'result' && product && (
                <div className="space-y-4">
                  {/* Product Card */}
                  <div className="bg-zinc-800/50 rounded-xl p-4">
                    <div className="flex gap-4">
                      {product.image_url ? (
                        <img src={product.image_url} alt="" className="w-24 h-24 object-cover rounded-lg" />
                      ) : (
                        <div className="w-24 h-24 bg-zinc-700 rounded-lg flex items-center justify-center">
                          <Package className="h-10 w-10 text-zinc-500" />
                        </div>
                      )}
                      <div className="flex-1">
                        <h3 className="font-bold text-white">{product.name}</h3>
                        {product.brand && <p className="text-sm text-zinc-400">{product.brand}</p>}
                        <p className="text-xs text-zinc-500 mt-1">Serving: {product.serving_size || '100g'}</p>
                        {product.nutriscore && (
                          <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs font-bold text-white ${getNutriScoreColor(product.nutriscore)}`}>
                            Nutri-Score {product.nutriscore.toUpperCase()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Servings */}
                  <div>
                    <label className="text-xs text-zinc-500 mb-2 block">Number of Servings</label>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setServings(Math.max(0.5, servings - 0.5))}
                        className="w-10 h-10 rounded-lg bg-zinc-800 text-white flex items-center justify-center hover:bg-zinc-700"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <input
                        type="number"
                        value={servings}
                        onChange={(e) => setServings(Math.max(0.5, parseFloat(e.target.value) || 1))}
                        step="0.5"
                        min="0.5"
                        className="w-20 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-center text-lg"
                      />
                      <button
                        onClick={() => setServings(servings + 0.5)}
                        className="w-10 h-10 rounded-lg bg-zinc-800 text-white flex items-center justify-center hover:bg-zinc-700"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Nutrition */}
                  <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-4">
                    <h4 className="text-sm font-semibold text-white mb-3">
                      Nutrition ({servings} serving{servings !== 1 ? 's' : ''})
                    </h4>
                    <div className="grid grid-cols-4 gap-3 text-center">
                      <div>
                        <div className="text-xl font-bold text-white">{scaledValue(product.calories)}</div>
                        <div className="text-xs text-zinc-400">Calories</div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-red-400">{scaledValue(product.protein)}g</div>
                        <div className="text-xs text-zinc-400">Protein</div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-yellow-400">{scaledValue(product.carbs)}g</div>
                        <div className="text-xs text-zinc-400">Carbs</div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-green-400">{scaledValue(product.fat)}g</div>
                        <div className="text-xs text-zinc-400">Fat</div>
                      </div>
                    </div>
                    {product.sugar !== null && (
                      <div className="mt-3 pt-3 border-t border-zinc-700 grid grid-cols-2 gap-3 text-center text-xs">
                        <div>
                          <span className="text-zinc-400">Sugar: </span>
                          <span className="text-white">{scaledValue(product.sugar)}g</span>
                        </div>
                        <div>
                          <span className="text-zinc-400">Fiber: </span>
                          <span className="text-white">{scaledValue(product.fiber)}g</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Meal Type */}
                  <div>
                    <label className="text-xs text-zinc-500 mb-2 block">Add to Meal</label>
                    <div className="grid grid-cols-4 gap-2">
                      {MEAL_TYPES.map(type => (
                        <button
                          key={type.id}
                          onClick={() => setMealType(type.id)}
                          className={`p-2 rounded-lg text-center transition-all ${
                            mealType === type.id
                              ? 'bg-blue-500/20 border-2 border-blue-500 text-white'
                              : 'bg-zinc-800 border-2 border-transparent text-zinc-400'
                          }`}
                        >
                          <span className="text-lg">{type.emoji}</span>
                          <p className="text-xs mt-1">{type.label}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={resetState}
                      className="flex-1 border-zinc-700"
                    >
                      Scan Another
                    </Button>
                    <Button
                      onClick={logProduct}
                      disabled={saving}
                      className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                      Log Product
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
